import { useEffect, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

export const TextInput = ({
  value,
  onChange,
  onBlur,
  label,
  iconClassName,
  placeholder,
  id,
  tooltip,

  inputRef,
  isDisabled,
  inputStyle,
}: {
  value?: string;
  onChange?: (newValue?: string) => void;
  onBlur?: (newValue?: string) => void;
  label?: string;
  iconClassName?: string;
  placeholder?: string;
  id?: string;
  tooltip?: string;

  inputRef?: React.RefObject<HTMLInputElement>;
  isDisabled?: boolean;
  inputStyle?: React.CSSProperties;
}) => {
  const ref = inputRef || useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.value = value !== undefined ? value : placeholder || '';
    }
  }, [value, placeholder]);

  return (
    <Container style={{ pointerEvents: isDisabled ? 'none' : undefined }}>
      {(label || iconClassName) && (
        <Label id={id} onClick={() => ref.current?.focus()}>
          {iconClassName && <i className={iconClassName} />}
          {label}
        </Label>
      )}
      {tooltip && id && <Tooltip anchorSelect={`#${id}`} place="bottom" content={tooltip} delayShow={1000} />}

      <InputField
        ref={ref}
        type="text"
        onFocus={(e) => e.target?.select()}
        onChange={(e) => onChange && onChange(e.target.value)}
        onBlur={(e) => {
          e.target.value === '' && onChange && onChange(undefined);
          onBlur && onBlur(e.target.value);
        }}
        spellCheck={false}
        $isComputed={value === undefined && placeholder !== undefined}
        disabled={isDisabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        style={inputStyle}
      />
    </Container>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 8px;

  border-radius: var(--radius-2);

  &:hover {
    outline: 1px solid var(--neutral-3);
  }

  &:focus-within {
    outline: 1px solid var(--primary-1);
  }
`;

const Label = styled.label`
  padding-right: 5px;

  color: var(--main-2);
  font-family: var(--font-2);
  font-size: 13px;
  white-space: nowrap;

  display: flex;
  align-items: center;

  i {
    width: 20px;
    font-size: 19px;
    translate: -2px 0;
  }
`;

const InputField = styled.input<{ $isComputed?: boolean }>`
  cursor: default;
  box-sizing: border-box;
  width: 100%;
  height: 25px;

  overflow: hidden;
  text-overflow: ellipsis;

  background-color: transparent;
  border: none;

  color: var(--main-1);
  font-family: var(--font-1);
  font-size: 11px;

  &:focus {
    outline: none;
  }

  &::selection {
    background: var(--primary-2);
    color: var(--main-1);
  }

  ${({ $isComputed }) =>
    $isComputed &&
    `
    color: var(--main-2);
    font-style: italic;
  `}
`;
